import { forwardRef, useCallback, useState } from 'react';
import { faCaretDown, faCaretRight, faFolderPlus, faPaste, faPen, faPlus } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Workspace } from '@workspace/domain/workspace';
import { useParams } from 'react-router-dom';
import { SidebarMenuItem } from './SidebarMenuItem';
import { ViewModelCreateModal } from '@workspace/view-model-folder/ui/ViewModelCreateModal';
import { useCreateViewModelFromClipboardPayloadHandler } from '@workspace/view-model-folder/hooks';
import { WorkspaceItemTitle } from './WorkspaceItemTitle';
import { WorkspaceEditModal } from '@user/pages/WorkspacePage/internal/WorkspaceEditModal';
import { SidebarMenuButton } from './SidebarMenuButton';
import { WorkspaceDeleteConfirmModal } from '@user/pages/WorkspacePage/internal/WorkspaceDeleteConfirmModal';
import type { RouteParams } from '@user/pages/RouteParams';
import { FolderCreateModal } from '@workspace/view-model-folder/ui/FolderCreateModal';
import { ViewModelOperation } from '@view-model/domain/view-model';
import { useActionLogSender } from '@framework/action-log';
import { Folder } from '@workspace/view-model-folder/domain/Folder';
import { useSidebarItemMenuPermissions } from './useSidebarItemMenuPermissions';

type Modals = 'ViewModelCreate' | 'WorkspaceEdit' | 'WorkspaceDelete' | 'FolderCreate';

// サイドバーのワークスペースのメニューから
const useHandleModals = () => {
  const [openModal, setOpenModal] = useState<Modals | null>(null);

  return {
    openModal,
    handleViewModelCreateModalOpen: useCallback(() => {
      setOpenModal('ViewModelCreate');
    }, []),
    handleWorkspaceEditModalOpen: useCallback(() => {
      setOpenModal('WorkspaceEdit');
    }, []),
    handleWorkspaceDeleteModalOpen: useCallback(() => {
      setOpenModal('WorkspaceDelete');
    }, []),
    handleFolderCreateModalOpen: useCallback(() => {
      setOpenModal('FolderCreate');
    }, []),
    handleModalClose: useCallback(() => {
      setOpenModal(null);
    }, []),
  };
};

type Props = {
  workspace: Workspace;
  isOpen: boolean;
  toggleWorkspace: () => void;
  isSelected: boolean;
  firstLoaded?: boolean;
  searchKeyword?: string;
  children?: React.ReactNode;
};

export const WorkspaceItem = forwardRef<HTMLDivElement, Props>(
  ({ workspace, isOpen, toggleWorkspace, isSelected, firstLoaded = true, searchKeyword, children }, ref) => {
    const actionLogSender = useActionLogSender();

    const { workspaceId: workspaceIdParams } = useParams<RouteParams>();
    const {
      openModal,
      handleViewModelCreateModalOpen,
      handleWorkspaceEditModalOpen,
      handleWorkspaceDeleteModalOpen,
      handleFolderCreateModalOpen,
      handleModalClose,
    } = useHandleModals();

    // ビューモデルをワークスペース直下に作成するメソッド
    const handleViewModelInRootSubmit = useCallback(
      async (name: string) => {
        await ViewModelOperation.createToRoot(workspace.id, name, actionLogSender);
        handleModalClose();
      },
      [actionLogSender, handleModalClose, workspace.id]
    );

    const createViewModelFromClipboardPayload = useCreateViewModelFromClipboardPayloadHandler(
      workspace.groupId,
      workspace.id,
      Folder.ROOT_ID
    );

    const { isEditMenuButtonShowable, isAdminMenuButtonShowable } = useSidebarItemMenuPermissions(workspace);

    return (
      <div className="relative" ref={ref}>
        <div className="flex items-center">
          <div className="mr-1">
            <button className="focus:outline-none" onClick={toggleWorkspace}>
              <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} className="size-4" />
            </button>
          </div>
          <SidebarMenuItem
            isSelected={isSelected}
            isMenuButtonShowable={isEditMenuButtonShowable}
            popupMenu={
              <>
                <SidebarMenuButton
                  handleClick={handleViewModelCreateModalOpen}
                  icon={faPlus}
                  title={'新規ビューモデル'}
                />
                <SidebarMenuButton
                  handleClick={handleFolderCreateModalOpen}
                  icon={faFolderPlus}
                  title={'新規フォルダ'}
                />
                <SidebarMenuButton
                  handleClick={createViewModelFromClipboardPayload}
                  icon={faPaste}
                  title={'ビューモデルをペースト'}
                />
                {isAdminMenuButtonShowable && (
                  <>
                    <SidebarMenuButton handleClick={handleWorkspaceEditModalOpen} icon={faPen} title={'編集'} />
                    <SidebarMenuButton
                      handleClick={handleWorkspaceDeleteModalOpen}
                      icon={faTrashAlt}
                      title={'削除'}
                      className="text-red-700"
                    />
                  </>
                )}
              </>
            }
          >
            <WorkspaceItemTitle workspace={workspace} searchKeyword={searchKeyword} />
          </SidebarMenuItem>
        </div>

        {/* ビューモデル作成モーダル */}
        {openModal === 'ViewModelCreate' && (
          <ViewModelCreateModal
            isOpen={openModal === 'ViewModelCreate'}
            onSubmit={handleViewModelInRootSubmit}
            onClose={handleModalClose}
          />
        )}

        {/* ワークスペース編集モーダル */}
        {openModal === 'WorkspaceEdit' && (
          <WorkspaceEditModal isOpen={openModal === 'WorkspaceEdit'} onClose={handleModalClose} workspace={workspace} />
        )}

        {/* ワークスペース削除モーダル */}
        {openModal === 'WorkspaceDelete' && (
          <WorkspaceDeleteConfirmModal
            isOpen={openModal === 'WorkspaceDelete'}
            onClose={handleModalClose}
            workspace={workspace}
            shouldMoveToGroupPage={workspaceIdParams === workspace.id}
          />
        )}

        {/* フォルダー作成モーダル */}
        {openModal === 'FolderCreate' && (
          <FolderCreateModal
            isOpen={openModal === 'FolderCreate'}
            onClose={handleModalClose}
            workspaceId={workspace.id}
            folderId={Folder.ROOT_ID}
          />
        )}

        {isOpen && firstLoaded && <div className="ml-4">{children}</div>}
      </div>
    );
  }
);

WorkspaceItem.displayName = 'WorkspaceItem';
